<template>
  <v-card v-if="ready" class="elevation-0">
    <v-card-text class="pa-1">
      <bar-chart
        title="Planned Warehouse Capacity"
        sub-title="Considering your operating hours, how much of that time you had appointments scheduled for"
        :data="plannedCapacityData"
        :categories="categories"
        :colors="colors"
        :stacked="true"
        :data-label-formatter="formatDataLabels"
        :show-data-labels="true">
      </bar-chart>

      <bar-chart
        class="mt-10"
        title="Fulfilled Warehouse Capacity"
        sub-title="Considering your operating hours, how much of that time the warehouse was occupied for. Comparing this chart with the chart above can help you identify delays in appointment completion"
        :data="fulfilledCapacityData"
        :categories="categories"
        :colors="colors"
        :stacked="true"
        :data-label-formatter="formatDataLabels"
        :show-data-labels="true">
      </bar-chart>

      <bar-chart
        class="mt-10"
        title="Unplanned Warehouse Extra Time"
        sub-title="Considering your operating hours, how much extra time was used? These are calculated based on the manual appointment status updates"
        :data="extraCapacityData"
        :categories="categories"
        show-for-single-series
        :colors="colors">
      </bar-chart>
    </v-card-text>
  </v-card>
  <v-skeleton-loader v-else class="mx-auto" type="card"></v-skeleton-loader>
</template>

<script>
import insightsMixin from '@/components/mixins/insightsMixin';

/**
 * Appointments by Status Bar Chart
 * @displayName Appointments by Status Bar Chart
 */
export default {
  mixins: [insightsMixin],
  data() {
    return {
      apiEndpoint: 'warehouse/capacity-usage',
      includeVolumeInUri: false,
      currentRequests: [],
      categories: [],
      colors: ['#2F8CF7', '#8ACEFF'],
      initialLookbackDays: 0,
      plannedCapacityData: [],
      fulfilledCapacityData: [],
      extraCapacityData: []
    };
  },
  methods: {
    formatXAxis(data) {
      return this.novaCore.ucWords(data);
    },
    async getData() {
      if (this.selectedWarehouse?.length) {
        this.queryParams.warehouseIds = this.novaCore.pluck(this.selectedWarehouse, 'id');
      } else {
        return;
      }
      const requestData = this.getQueryParams();
      delete requestData.warehouseId;
      const url = `/metrics/${this.apiEndpoint}`;
      const semaphoreKey = encodeURI(`${url}-${JSON.stringify(requestData)}`);

      if (this.currentRequests.includes(semaphoreKey)) {
        return;
      }
      this.currentRequests.push(semaphoreKey);

      axios
        .post(url, requestData)
        .then(response => {
          if (response?.data) {
            this.rawData = response.data.data;
          }
        })
        .finally(
          () => (this.currentRequests = this.currentRequests.filter(r => r !== semaphoreKey))
        );
    },
    formatData() {
      this.plannedCapacityData = [];
      this.fulfilledCapacityData = [];
      this.extraCapacityData = [];

      const tempData = {
        occupiedTime: [],
        idleTime: [],
        plannedSchedulePct: [],
        plannedIdlePct: [],
        unplannedExtraTime: []
      };

      this.categories = this.rawData.map(wh => {
        tempData.occupiedTime.push(wh.occupiedPct);
        tempData.idleTime.push(wh.idlePct);
        tempData.plannedSchedulePct.push(wh.plannedSchedulePct);
        tempData.plannedIdlePct.push(wh.plannedIdlePct);
        tempData.unplannedExtraTime.push(wh.extraTimeInMinutes);
        return wh.warehouseName;
      });

      if (tempData.occupiedTime.length && tempData.idleTime.length) {
        this.fulfilledCapacityData.push({
          name: 'Actual Occupied Time',
          data: tempData.occupiedTime
        });
        this.fulfilledCapacityData.push({
          name: 'Actual Idle Time',
          data: tempData.idleTime
        });
      }

      if (tempData.plannedIdlePct.length && tempData.plannedSchedulePct.length) {
        this.plannedCapacityData.push({
          name: 'Scheduled Appointments',
          data: tempData.plannedSchedulePct
        });
        this.plannedCapacityData.push({
          name: 'Planned Idle Time',
          data: tempData.plannedIdlePct
        });
      }

      if (tempData.unplannedExtraTime.length) {
        this.extraCapacityData.push({
          name: 'Minutes spent over warehouse availability',
          data: tempData.unplannedExtraTime
        });
      }

      this.ready = true;
    },
    formatDataLabels(v) {
      return `${v}%`;
    }
  }
};
</script>
